/**
 * Phase 4 slice 4 — Snags list. Each snag has a linked Kanban task; closing the
 * snag here also completes its task (two-way sync handled server-side).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ClipboardList, CheckCircle2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut, apiPost } from '../../lib/api';
import { toast } from 'sonner';

const SEV_TONE = {
  low: 'bg-slate-100 text-slate-700 border-slate-200', medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200', critical: 'bg-red-50 text-red-700 border-red-200',
};
const STATUS_TONE = {
  open: 'bg-red-50 text-red-700 border-red-200', in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  closed: 'bg-emerald-50 text-emerald-700 border-emerald-200', waived: 'bg-slate-100 text-slate-600 border-slate-200',
};
import { formatDate as fmt } from '../../lib/format';

const TRADES = ['Civil', 'MEP', 'Joinery / Carpentry', 'Painting', 'Flooring', 'Facade', 'Plumbing', 'Electrical', 'HVAC', 'False Ceiling', 'Glass & Aluminium', 'General'];

export default function Snags() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [filterTrade, setFilterTrade] = useState('');
  const [filterRound, setFilterRound] = useState('');
  const [rows, setRows] = useState([]);

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects')); }, []);
  const reload = () => {
    const q = [
      projectId && `project_id=${projectId}`,
      status && `status=${status}`,
      severity && `severity=${severity}`,
      filterTrade && `trade=${encodeURIComponent(filterTrade)}`,
      filterRound && `snag_round=${filterRound}`,
    ].filter(Boolean).join('&');
    apiGet(`/quality/snags${q ? `?${q}` : ''}`).then((d) => setRows(d.snags || [])).catch(() => {});
  };
  useEffect(reload, [projectId, status, severity, filterTrade, filterRound]); // eslint-disable-line

  const closeSnag = async (e, snag) => {
    e.stopPropagation();
    try { await apiPut(`/quality/snags/${snag.id}`, { status: 'closed' }); toast.success('Snag closed — linked task completed'); reload(); }
    catch { toast.error('Failed to close'); }
  };

  const reInspect = async (e, snag) => {
    e.stopPropagation();
    try {
      const r = await apiPut(`/quality/snags/${snag.id}/re-inspect`, {});
      toast.success(`Re-opened for Round ${r.snag_round} inspection`);
      reload();
    } catch { toast.error('Failed to re-open'); }
  };

  const ROUND_LABELS = { 1: '1st', 2: '2nd', 3: 'De-snag', 4: '4th' };

  return (
    <div>
      <PageHeader overline="Quality" title="Snags" subtitle="Finishing-stage defects — area · trade · round tracking for interiors"
        actions={(
          <button onClick={() => navigate('/app/quality/snags/new')} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="snag-new-nav"><Plus size={14} /> Raise Snag</button>
        )}
      />
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[220px]" data-testid="snag-filter-project">
          <option value="">All projects</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="snag-filter-status">
          <option value="">All statuses</option>{Object.keys(STATUS_TONE).map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="snag-filter-severity">
          <option value="">All severities</option>{Object.keys(SEV_TONE).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterTrade} onChange={(e) => setFilterTrade(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="snag-filter-trade">
          <option value="">All trades</option>{TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterRound} onChange={(e) => setFilterRound(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="snag-filter-round">
          <option value="">All rounds</option>
          {[1,2,3,4].map((r) => <option key={r} value={r}>{ROUND_LABELS[r] || `Round ${r}`}</option>)}
        </select>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full dense-table" data-testid="snag-table">
            <thead><tr><th>Area / Room</th><th>Location</th><th>Description</th><th>Trade</th><th>Round</th><th>Severity</th><th>Assigned</th><th>Due</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={10} className="text-center text-sm text-slate-400 py-10"><ClipboardList size={20} className="inline mr-2 opacity-40" />No snags.</td></tr>
                : rows.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50" data-testid={`snag-row-${s.id}`}>
                    <td className="text-[12px] text-slate-600 truncate max-w-[120px]">{s.area || '—'}</td>
                    <td className="text-[13px] font-display font-semibold max-w-[150px] truncate">{s.location_on_site || '—'}</td>
                    <td className="text-[13px] max-w-[220px] truncate">{s.description}</td>
                    <td className="text-[11px] text-slate-500">{s.trade || '—'}</td>
                    <td className="text-center">
                      {s.snag_round && <span className="text-[10px] font-bold text-slate-500">{ROUND_LABELS[s.snag_round] || `R${s.snag_round}`}</span>}
                    </td>
                    <td><span className={`tag border ${SEV_TONE[s.severity]}`}>{s.severity}</span></td>
                    <td className="text-xs text-slate-500">{s.assigned_to || '—'}</td>
                    <td className="text-xs text-slate-500">{fmt(s.due_date)}</td>
                    <td><span className={`tag border ${STATUS_TONE[s.status]}`}>{s.status}</span></td>
                    <td className="flex items-center gap-1">
                      {['open', 'in_progress'].includes(s.status) && (
                        <button onClick={(e) => closeSnag(e, s)} className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:underline" data-testid={`snag-close-${s.id}`}>
                          <CheckCircle2 size={12} /> Close
                        </button>
                      )}
                      {['closed', 'waived'].includes(s.status) && (
                        <button onClick={(e) => reInspect(e, s)} className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 hover:underline" data-testid={`snag-reinspect-${s.id}`}>
                          Re-inspect
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
